import {
  ALFRED_ATTACHMENT_MAX_BYTES,
  ALFRED_ATTACHMENT_TYPES,
  ALFRED_ATTACHMENTS_PER_TICKET_MAX,
} from "../lib/limits.ts";

import { formatBytes } from "./alfred.ts";
import { ALFRED_RULES_TEXTS } from "./texts.ts";

/**
 * Lo que el API acepta como adjunto de Alfred, dicho antes de subir: el
 * tipo, el tamaño y cuántos lleva un pedido (`lib/limits.ts`). Funciones
 * puras; el API lo vuelve a validar y su error se dice en cada archivo.
 */

/** Para el `accept` del selector: los tipos y sus extensiones. */
export const ALFRED_ACCEPT = [
  ...Object.keys(ALFRED_ATTACHMENT_TYPES),
  ...Object.values(ALFRED_ATTACHMENT_TYPES).map((extension) => `.${extension}`),
  /* `.jpeg` también es JPG. */
  ".jpeg",
].join(",");

const BY_EXTENSION = new Map<string, string>([
  ...Object.entries(ALFRED_ATTACHMENT_TYPES).map(
    ([type, extension]) => [extension, type] as [string, string],
  ),
  ["jpeg", "image/jpeg"],
]);

/** El navegador no siempre sabe el tipo: vacío, o el genérico. */
const UNKNOWN_TYPES = new Set(["", "application/octet-stream"]);

const extensionOf = (name: string): string =>
  /\.([a-z0-9]+)$/i.exec(name)?.[1]?.toLowerCase() ?? "";

const RULES_TEXT = {
  type: ALFRED_RULES_TEXTS.type,
  size: (name: string, bytes: number) =>
    ALFRED_RULES_TEXTS.size(
      name,
      formatBytes(bytes),
      formatBytes(ALFRED_ATTACHMENT_MAX_BYTES),
    ),
  count: (name: string) =>
    ALFRED_RULES_TEXTS.count(name, ALFRED_ATTACHMENTS_PER_TICKET_MAX),
} as const;

/**
 * El archivo con un tipo que el API acepta, o `null` si no lo tiene. Si el
 * navegador no lo sabe, se saca de la extensión, y el archivo se rehace con
 * él: el API mira el tipo de la parte multipart, no la extensión.
 */
export const acceptedFile = (file: File): File | null => {
  if (file.type in ALFRED_ATTACHMENT_TYPES) return file;
  if (!UNKNOWN_TYPES.has(file.type)) return null;
  const type = BY_EXTENSION.get(extensionOf(file.name));
  return type === undefined
    ? null
    : new File([file], file.name, { type, lastModified: file.lastModified });
};

export interface Checked {
  readonly accepted: readonly File[];
  /** Por qué no entró cada uno de los que no entraron, para decirlo. */
  readonly rejected: readonly string[];
}

/**
 * Cuáles de `files` se pueden agregar, si quedan `room` adjuntos libres en
 * el pedido. Los que no caben son los últimos: se agregan en orden.
 */
export const checkFiles = (files: readonly File[], room: number): Checked => {
  const accepted: File[] = [];
  const rejected: string[] = [];
  for (const file of files) {
    const typed = acceptedFile(file);
    if (typed === null) {
      rejected.push(RULES_TEXT.type(file.name));
    } else if (file.size > ALFRED_ATTACHMENT_MAX_BYTES) {
      rejected.push(RULES_TEXT.size(file.name, file.size));
    } else if (accepted.length >= room) {
      rejected.push(RULES_TEXT.count(file.name));
    } else {
      accepted.push(typed);
    }
  }
  return { accepted, rejected };
};

/** Los adjuntos vivos de un pedido: los suyos y los de sus comentarios. */
export const attachmentsCountOf = (ticket: {
  readonly attachments: readonly unknown[];
  readonly comments: readonly { readonly attachments: readonly unknown[] }[];
}): number =>
  ticket.attachments.length +
  ticket.comments.reduce(
    (total, comment) => total + comment.attachments.length,
    0,
  );
